/**
 * iCIMS ships each location as one dirty string with two or three `" - "`
 * segments, for example:
 *
 *   "Yokohama - Japan -   Yokohama, Kanagawa 220-8114 Japan"
 *   "Remote - Remote"
 *
 * Measured over 522 elements on 2026-08-06: 398 carry three segments, 124 carry
 * two, and 304 contain a double space. The third segment is a full address and
 * no surface renders it.
 *
 * The listing and the job details page format the SAME array two different
 * ways, on purpose. A listing row holds one line in a narrow column. A details
 * subheading holds every location.
 *
 * The details side already owns its own formatter, `formatIcimsLocation` in
 * `job-posting-subheading/src/utils/`, merged under GWP-12935. That function
 * deliberately does not serve the gallery, and this module deliberately does not
 * serve the details page.
 *
 * CAUTION: both modules split on `" - "` and both normalize whitespace the same
 * way. That duplication is known and accepted for now. If the iCIMS format
 * changes, BOTH need the edit.
 */

const SEGMENT_SEPARATOR = ' - ';
const REMOTE_CITY = 'Remote';

export interface IcimsLocation {
	/** First segment. A city name, or the literal `Remote`. */
	readonly city: string;
	/** Second segment, when present. A country, a region, or `Remote`. */
	readonly country: string | undefined;
}

/** Splits one raw iCIMS location string. Never throws. Drops empty segments. */
export function parseIcimsLocation(raw: string): IcimsLocation {
	const segments = raw
		.split(SEGMENT_SEPARATOR)
		.map((segment) => segment.replace(/\s+/g, ' ').trim())
		.filter((segment) => segment.length > 0);

	return { city: segments[0] ?? '', country: segments[1] };
}

/**
 * The one-line summary a listing row shows. Verified against all 219 rows of
 * `https://www.atlassian.com/company/careers/all-jobs` on 2026-08-06:
 *
 *   []                                    -> ""
 *   ["Sydney - Australia - …"]            -> "Sydney"
 *   ["Seattle - …", "Remote - Remote"]    -> "Seattle or Remote"
 *   ["Remote - France -     France"]      -> "Remote or Remote"
 *
 * The last case repeats the word, and it is a defect in the Magnolia reference
 * that 41 of 219 rows show. This function reproduces it deliberately, so the
 * migrated page does not change copy while it moves. Read the note in
 * `test/icims-location.spec.ts` before you "fix" it.
 */
export function formatLocationSummary(locations: readonly string[]): string {
	const cities = locations.map((raw) => parseIcimsLocation(raw).city).filter((city) => city !== '');
	const first = cities[0];
	if (first === undefined) return '';

	const hasRemote = cities.includes(REMOTE_CITY);
	return hasRemote ? `${first} or ${REMOTE_CITY}` : first;
}
